import { useInfiniteQuery } from '@tanstack/react-query';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { IndustryEnum } from '@dashboard/enums/industry';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import plans from '@dashboard/services/plans';
import { fetchInfinite } from '@dashboard/services/request';

import { FormElement, Label, Select, TextInput } from '@dashboard/components/Form';
import Spinner from '@dashboard/components/Spinner';

import UsersTable from '../components/UsersTable';

const planHandles = plans.reduce((acc, plan) => {
  if (!acc.find(p => p.handle === plan.handle)) {
    acc.push(plan);
  }

  return acc;
}, []);

const Merchants = () => {
  const { token } = useAuth();
  const history = useHistory();

  const urlQuery = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput();
  const [sortBy, setSortBy] = useState(urlQuery.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(urlQuery.sortDirection || -1);
  const [limit] = useState(20);
  const [plan, setPlan] = useState(urlQuery.plan || '');
  const [billingPeriod, setBillingPeriod] = useState(urlQuery.billingPeriod || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(urlQuery.subscriptionStatus || '');
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState(
    urlQuery.stripeSubscriptionStatus || '',
  );

  const [industry, setIndustry] = useState(urlQuery.industry || '');
  const [hasCustomPricing, setHasCustomPricing] = useState(urlQuery.hasCustomPricing || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(plan && { plan }),
        ...(billingPeriod && { billingPeriod }),
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(stripeSubscriptionStatus && { stripeSubscriptionStatus }),
        ...(industry && { industry }),
        ...(hasCustomPricing && { hasCustomPricing }),
        ...(search && { search }),
      }),
    });
  }, [
    sortBy,
    sortDirection,
    plan,
    subscriptionStatus,
    stripeSubscriptionStatus,
    industry,
    hasCustomPricing,
    search,
    billingPeriod,
  ]);

  const query = {
    ...(plan && {
      'seller.plan.handle': plan,
    }),
    ...(billingPeriod && {
      'seller.plan.billingPeriod': billingPeriod,
    }),
    ...(subscriptionStatus && {
      'seller.subscriptionStatus': subscriptionStatus,
    }),
    ...(stripeSubscriptionStatus && {
      'seller.stripeSubscriptionStatus': stripeSubscriptionStatus,
    }),
    ...(industry && { 'seller.industry': industry }),
    ...(hasCustomPricing === 'true' && {
      'seller.plan.volumeLimit': JSON.stringify({ $exists: true, $ne: null }),
    }),
    ...(hasCustomPricing === 'false' && {
      $or: JSON.stringify([
        { 'seller.plan.volumeLimit': { $exists: false } },
        { 'seller.plan.volumeLimit': null },
      ]),
    }),
    search,
    limit,
    sort: JSON.stringify({
      [sortBy]: sortDirection,
    }),
  };

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['merchants', query],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/merchants?${stringify({
          ...query,
          limit: limit,
          skip: pageParam * limit,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 60 * 24,
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <React.Fragment>
      <div className="flex justify-end items-end px-4">
        <div className="pb-0 flex gap-x-2">
          <FormElement className="!mb-0">
            <Label htmlFor="industry">Store focus</Label>
            <Select
              name="industry"
              label="Store focus"
              onChange={e => setIndustry(e.target.value)}
              className="w-32"
            >
              <option value="">All</option>
              {Object.values(IndustryEnum).map(currentAccountType => (
                <option
                  key={currentAccountType}
                  value={currentAccountType}
                  selected={currentAccountType === industry}
                >
                  {upperFirst(currentAccountType)}
                </option>
              ))}
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="plan">Plan</Label>
            <Select
              name="plan"
              label="Plan"
              onChange={e => {
                setPlan(e.target.value);

                if (!subscriptionStatus) {
                  setSubscriptionStatus('active');
                }
              }}
              className="w-32"
              value={plan}
            >
              <option value="">All</option>
              {planHandles.map(plan => (
                <option key={plan.handle} value={plan.handle} className="">
                  {plan.title}
                </option>
              ))}
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="billingPeriod">Billing period</Label>
            <Select
              name="billingPeriod"
              label="Billing period"
              onChange={e => {
                setBillingPeriod(e.target.value);

                if (!subscriptionStatus) {
                  setSubscriptionStatus('active');
                }
              }}
              className="w-32"
              value={billingPeriod}
            >
              <option value="">All</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="subscriptionStatus">Subscription status</Label>
            <Select
              name="subscriptionStatus"
              label="Subscription status"
              onChange={e => setSubscriptionStatus(e.target.value)}
              className="!w-40"
              value={subscriptionStatus}
            >
              <option value="">All</option>
              <option value="trialing">Trialing</option>
              <option value="active">Active</option>
              <option value="trial_expired">Trial expired</option>
              <option value="canceled">Canceled</option>
              <option value="legacy">Legacy</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="stripeSubscriptionStatus">Stripe renewal status</Label>
            <Select
              name="stripeSubscriptionStatus"
              label="Stripe subscription status"
              onChange={e => setStripeSubscriptionStatus(e.target.value)}
              className="w-32"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="unpaid">Unpaid</option>
              <option value="canceled">Canceled</option>
              <option value="incomplete">Incomplete</option>
              <option value="trialing">Trialing</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="hasCustomPricing">Custom pricing</Label>
            <Select
              name="hasCustomPricing"
              label="Custom pricing"
              onChange={e => setHasCustomPricing(e.target.value)}
              className="w-32"
              value={hasCustomPricing}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="search">Search</Label>
            <div className="w-64">
              <TextInput
                border
                name="search"
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by name, email, ID"
                value={searchInput}
              />
            </div>
          </FormElement>
        </div>
      </div>

      <UsersTable
        data={data}
        onSort={onSort}
        sortBy={sortBy}
        sortDirection={sortDirection}
        getNext={fetchNextPage}
      />

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </React.Fragment>
  );
};

export default Merchants;
